import { scopedApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { EndpointResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../requestOptions';

export interface PreviewProductOption {
  id: number;
  name: string;
  sku: string;
}

export interface SearchPreviewProductsArgs {
  q: string;
  limit?: number;
}

const DEFAULT_LIMIT = 10;
const MAX_LIMIT = 50;

function normalizeProduct(raw: unknown): PreviewProductOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const record = raw as Record<string, unknown>;
  const id = typeof record.id === 'number' ? record.id : Number(record.id);
  if (!Number.isFinite(id) || id <= 0) return null;
  const name = typeof record.name === 'string' && record.name.trim().length > 0 ? record.name : `Product ${id}`;
  const sku = typeof record.sku === 'string' ? record.sku : '';
  return { id, name, sku };
}

export async function searchPreviewProducts(
  scope: RequestScope,
  args: SearchPreviewProductsArgs,
  options: ReadRequestOptions
): Promise<EndpointResponse<PreviewProductOption[]>> {
  const term = args.q.trim();
  if (term.length === 0) {
    return { result: [], status: 200, execution_time: 0, num_results: 0 };
  }
  const limit = Math.max(1, Math.min(MAX_LIMIT, args.limit ?? DEFAULT_LIMIT));

  try {
    type SearchEnvelope = { result?: unknown[]; execution_time?: number; num_results?: number };
    const response = await scopedApi(scope).get<SearchEnvelope>('/search/catalog', {
      ...options,
      params: {
        page: 1,
        limit,
        q: term,
        group: 'product',
        sort: 'id:1',
      },
    });
    const raw = Array.isArray(response.data?.result) ? response.data.result : [];
    const result: PreviewProductOption[] = raw
      .map(normalizeProduct)
      .filter((p: PreviewProductOption | null): p is PreviewProductOption => p !== null);
    return {
      result,
      status: response.status,
      execution_time: typeof response.data?.execution_time === 'number' ? response.data.execution_time : 0,
      num_results: typeof response.data?.num_results === 'number' ? response.data.num_results : result.length,
    };
  } catch (e) {
    // The search term is user-typed — redact request data so it doesn't leak
    // through Bugsnag metadata on transient backend failures.
    return utils.error.handleAxiosError(e, { redactRequestData: true });
  }
}
